import type { CreatorProfile } from '@/mocks/profiles';
import SAR from '@/components/feature/SAR';

interface ProfileSidebarProps {
  profile: CreatorProfile;
}

const recentSupporters = [
  { name: 'عبدالرحمن الغامدي', amount: 500, time: 'منذ ساعتين', avatar: 'https://readdy.ai/api/search-image?query=Saudi%20man%20portrait%20clean%20background%20warm&width=60&height=60&seq=501&orientation=squarish' },
  { name: 'منى الشهري', amount: 1000, time: 'منذ يوم', avatar: 'https://readdy.ai/api/search-image?query=Saudi%20woman%20hijab%20portrait%20clean%20background&width=60&height=60&seq=502&orientation=squarish' },
  { name: 'فهد العنزي', amount: 300, time: 'منذ يومين', avatar: 'https://readdy.ai/api/search-image?query=Saudi%20man%20portrait%20professional%20clean%20background&width=60&height=60&seq=503&orientation=squarish' },
  { name: 'داعم مجهول', amount: 200, time: 'منذ 3 أيام', avatar: '' },
  { name: 'ريم السلمي', amount: 750, time: 'منذ 4 أيام', avatar: 'https://readdy.ai/api/search-image?query=Saudi%20woman%20portrait%20clean%20background%20elegant&width=60&height=60&seq=504&orientation=squarish' },
];

const ProfileSidebar = ({ profile }: ProfileSidebarProps) => {
  const formatAmount = (val: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(val);

  const progressToNextGoal = Math.min((profile.stats.totalRaised / 50000) * 100, 100);

  return (
    <div className="space-y-5">
      {/* Trust Score Card */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5">
        <h3 className="font-bold text-stone-800 text-sm mb-4 flex items-center gap-2">
          <i className="ri-shield-star-line text-emerald-600"></i>
          مستوى الثقة
        </h3>
        <div className="flex items-center gap-3 mb-4">
          <div className="relative w-16 h-16 shrink-0">
            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="26" fill="none" stroke="#f5f5f4" strokeWidth="6" />
              <circle
                cx="32" cy="32" r="26"
                fill="none"
                stroke="#10b981"
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 26}`}
                strokeDashoffset={`${2 * Math.PI * 26 * (1 - profile.stats.successRate / 100)}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-black text-stone-800">{profile.stats.successRate}%</span>
            </div>
          </div>
          <div>
            <div className="font-bold text-stone-800 text-sm">مبدع موثوق</div>
            <div className="text-stone-400 text-xs mt-0.5">بناءً على {profile.stats.campaignsCount} حملات</div>
          </div>
        </div>

        <div className="space-y-2.5">
          {[
            { label: 'هوية موثقة', done: true },
            { label: 'حساب بنكي مرتبط', done: true },
            { label: 'رقم جوال مؤكد', done: true },
            { label: 'بريد إلكتروني مؤكد', done: true },
            { label: 'ترخيص رسمي', done: false },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 ${item.done ? 'bg-emerald-100' : 'bg-stone-100'}`}>
                <i className={`text-xs ${item.done ? 'ri-check-line text-emerald-600' : 'ri-time-line text-stone-400'}`}></i>
              </div>
              <span className={`text-xs ${item.done ? 'text-stone-700' : 'text-stone-400'}`}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Progress to Next Milestone */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5">
        <h3 className="font-bold text-stone-800 text-sm mb-3 flex items-center gap-2">
          <i className="ri-trophy-line text-amber-500"></i>
          الهدف القادم
        </h3>
        <div className="flex items-end justify-between mb-2">
          <span className="text-xs text-stone-500"><SAR amount={50000} iconSize={11} /></span>
          <span className="text-xs font-bold text-emerald-600">{Math.round(progressToNextGoal)}%</span>
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-l from-emerald-500 to-emerald-400 rounded-full transition-all duration-700"
            style={{ width: `${progressToNextGoal}%` }}
          />
        </div>
        <p className="text-xs text-stone-400">
          <SAR amount={profile.stats.totalRaised} iconSize={11} /> من <SAR amount={50000} iconSize={11} />
        </p>
      </div>

      {/* Recent Supporters */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5">
        <h3 className="font-bold text-stone-800 text-sm mb-4 flex items-center gap-2">
          <i className="ri-user-heart-line text-rose-500"></i>
          آخر الداعمين
        </h3>
        <div className="space-y-3">
          {recentSupporters.map((supporter, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden bg-stone-100 shrink-0 flex items-center justify-center">
                {supporter.avatar ? (
                  <img src={supporter.avatar} alt={supporter.name} className="w-full h-full object-cover object-top" />
                ) : (
                  <i className="ri-user-line text-stone-400 text-sm"></i>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-stone-700 truncate">{supporter.name}</div>
                <div className="text-xs text-stone-400">{supporter.time}</div>
              </div>
              <span className="text-xs font-bold text-emerald-600 whitespace-nowrap"><SAR amount={supporter.amount} iconSize={11} /></span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Support CTA */}
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-5 text-white text-center">
        <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mx-auto mb-3">
          <i className="ri-heart-3-line text-xl text-white"></i>
        </div>
        <h3 className="font-bold text-sm mb-1">ادعم {profile.displayName}</h3>
        <p className="text-emerald-100 text-xs mb-4 leading-relaxed">كل ريال يُقرّبه من تحقيق حلمه</p>
        <button className="w-full bg-white text-emerald-700 font-bold text-sm py-2.5 rounded-xl hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap">
          تبرع الآن
        </button>
      </div>
    </div>
  );
};

export default ProfileSidebar;
